<script setup>
import { ref, reactive, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Button from 'dashboard/components-next/button/Button.vue';
import ProductInfoTabs from './ProductInfoTabs.vue';
import InformationTab from './tabs/InformationTab.vue';
import MediaTab from './tabs/MediaTab.vue';
import TestimonialTab from './tabs/TestimonialTab.vue';
import AnswerTemplateTab from './tabs/AnswerTemplateTab.vue';

const props = defineProps({
  assistantId: {
    type: [Number, String],
    required: true,
  },
  productName: {
    type: String,
    default: 'Product',
  },
});

const emit = defineEmits(['close']);

const { t } = useI18n();
const store = useStore();

// State
const isLoading = ref(false);
const useCompanyInfo = ref(true);
const selectedCountry = ref('');
const selectedLanguage = ref('');

// Data
const productInfo = reactive({
  series: '',
  description: '',
  overview: '',
  keyIngredients: '',
  functions: '',
  targetAudience: '',
  usage: '',
  packaging: '',
});

const mediaItems = ref([]);
const testimonials = ref([]);
const certificates = ref([]);
const priceInfo = reactive({
  currency: 'MYR',
  price: '',
  discountPrice: '',
  packages: [],
});
const answerTemplates = ref([]);

// Mock data for demo
const loadMockData = () => {
  // Product Info
  productInfo.series = 'Aemis';
  productInfo.description =
    'RELAZZ is a botanical beverage specially formulated to enhance sleep quality and relaxation. It contains milk protein hydrolysate (Lactium®), fermented rice germ extract, and L-theanine from botanical sources, which work together to promote calmness, reduce stress, and support restful sleep.';
  productInfo.overview =
    'Botanical beverage designed to promote relaxation, relieve stress, and improve sleep quality with a refreshing kiwi taste.';
  productInfo.keyIngredients =
    'Milk protein hydrolysate (Lactium®), fermented rice germ extract, L-theanine.';
  productInfo.functions =
    'Promotes relaxation, reduces stress, improves sleep quality, and supports mood balance.';

  // Media
  mediaItems.value = [
    {
      id: 1,
      title: 'Pain_Points_3',
      description: 'Tossing and Turning',
      category: 'pain_points',
      thumbnailUrl: '',
      createdAt: '2025-09-25T15:14:00',
      updatedAt: '2025-09-26T09:10:00',
    },
    {
      id: 2,
      title: 'RELAZZ_USP_1',
      description: '15 Years',
      category: 'usp',
      thumbnailUrl: '',
      createdAt: '2025-09-25T15:14:00',
      updatedAt: '2025-09-26T09:10:00',
    },
    {
      id: 3,
      title: 'RELAZZ_Benefits_1',
      description: 'Benefits',
      category: 'benefits',
      thumbnailUrl: '',
      createdAt: '2025-09-25T15:14:00',
      updatedAt: '2025-09-26T09:10:00',
    },
  ];

  // Testimonials
  testimonials.value = [
    {
      id: 1,
      title: 'Relazz Testimonial (English version)',
      content:
        'The customer said that he had better sleep quality and no more dreams at night.',
      language: 'English',
      thumbnailUrl: '',
      createdAt: '2024-02-22T11:38:00',
      updatedAt: '2024-03-01T15:37:00',
    },
    {
      id: 2,
      title: 'Relazz Testimonial (English version)',
      content:
        'The customer was having trouble sleeping. They tried many things, but nothing worked. Then, they tried Relazz and it worked within an hour.',
      language: 'English',
      thumbnailUrl: '',
      createdAt: '2024-02-22T11:38:00',
      updatedAt: '2024-03-01T15:37:00',
    },
    {
      id: 3,
      title: 'Relazz Testimonial (Chinese version)',
      content:
        '客户说他以前很难入睡也很难保持睡眠，但服用 Relazz 后，他可以睡个好觉。',
      language: '中文',
      thumbnailUrl: '',
      createdAt: '2024-02-22T11:38:00',
      updatedAt: '2024-03-01T15:37:00',
    },
  ];

  // Answer Templates
  answerTemplates.value = [
    {
      id: 1,
      question: 'What is RELAZZ?',
      answer:
        'RELAZZ is a botanical beverage specially formulated to enhance sleep quality and relaxation.',
    },
    {
      id: 2,
      question: 'How to take RELAZZ?',
      answer:
        'Take 1 sachet before bedtime. Mix with 150ml of warm or cold water.',
    },
  ];
};

// Handlers
const handleRefresh = () => {
  loadMockData();
  useAlert('Data refreshed successfully');
};

const handleSaveProductInfo = async data => {
  isLoading.value = true;
  try {
    Object.assign(productInfo, data);
    useAlert('Product information saved successfully');
  } catch (error) {
    useAlert(error.message || 'Failed to save product information');
  } finally {
    isLoading.value = false;
  }
};

// Media handlers
const handleAddMedia = () => {
  useAlert('Add media dialog - to be implemented');
};

const handleEditMedia = item => {
  useAlert(`Edit media: ${item.title}`);
};

const handleDeleteMedia = item => {
  useAlert(`Delete media: ${item.title}`);
};

// Testimonial handlers
const handleAddTestimonial = () => {
  useAlert('Add testimonial dialog - to be implemented');
};

const handleEditTestimonial = item => {
  useAlert(`Edit testimonial: ${item.title}`);
};

const handleDeleteTestimonial = item => {
  useAlert(`Delete testimonial: ${item.title}`);
};

// Answer Template handlers
const handleAddTemplate = () => {
  useAlert('Add template dialog - to be implemented');
};

const handleEditTemplate = item => {
  useAlert(`Edit template: ${item.question}`);
};

const handleDeleteTemplate = item => {
  useAlert(`Delete template: ${item.question}`);
};

const handleImportCsv = () => {
  useAlert('Import CSV - to be implemented');
};

const handleExportCsv = () => {
  useAlert('Export CSV - to be implemented');
};

onMounted(() => {
  loadMockData();
});
</script>

<template>
  <div class="p-6 bg-n-slate-1 min-h-screen">
    <ProductInfoTabs
      v-model:use-company-info="useCompanyInfo"
      v-model:selected-country="selectedCountry"
      v-model:selected-language="selectedLanguage"
      :product-name="productName"
      @refresh="handleRefresh"
    >
      <!-- Information Tab -->
      <template #information>
        <InformationTab
          :product-info="productInfo"
          :is-loading="isLoading"
          @save="handleSaveProductInfo"
        />
      </template>

      <!-- Media Tab -->
      <template #media>
        <MediaTab
          :media-items="mediaItems"
          :is-loading="isLoading"
          @add="handleAddMedia"
          @edit="handleEditMedia"
          @delete="handleDeleteMedia"
        />
      </template>

      <!-- Testimonial Tab -->
      <template #testimonial>
        <TestimonialTab
          :testimonials="testimonials"
          :is-loading="isLoading"
          @add="handleAddTestimonial"
          @edit="handleEditTestimonial"
          @delete="handleDeleteTestimonial"
        />
      </template>

      <!-- Certificate Tab -->
      <template #certificate>
        <div class="py-8 text-center text-n-slate-10">
          <span class="i-lucide-award w-16 h-16 mx-auto mb-4 opacity-50" />
          <p>Certificate management - coming soon</p>
        </div>
      </template>

      <!-- Price Tab -->
      <template #price>
        <div class="py-8 text-center text-n-slate-10">
          <span
            class="i-lucide-dollar-sign w-16 h-16 mx-auto mb-4 opacity-50"
          />
          <p>Price management - coming soon</p>
        </div>
      </template>

      <!-- Answer Template Tab -->
      <template #answerTemplate>
        <AnswerTemplateTab
          :templates="answerTemplates"
          :is-loading="isLoading"
          @add="handleAddTemplate"
          @edit="handleEditTemplate"
          @delete="handleDeleteTemplate"
          @import-csv="handleImportCsv"
          @export-csv="handleExportCsv"
        />
      </template>
    </ProductInfoTabs>
  </div>
</template>
